/**
 * Admin KPI Dashboard — platform-wide metrics, country readiness, engagement, monitoring.
 * Accessible to admin-role users only at /admin.
 */
import { useState, useEffect } from "react";
import { api } from "@/lib/api.ts";
import { HealthRing } from "@/components/shared/HealthRing.tsx";
import { ErrorMessage } from "@/components/shared/ErrorMessage.tsx";
import {
  LayoutDashboard, Globe, BarChart3, MessageSquare, Radio, Cpu,
  CheckCircle, AlertTriangle, XCircle, Users, FolderOpen, FileText,
  Building2, ThumbsUp, ThumbsDown, Mail, Sun, Moon
} from "lucide-react";
import { useTheme } from "@/lib/theme-context.tsx";

// ─── Inline helpers ───

function StatCard({ label, value, sub, icon: Icon }: { label: string; value: string | number; sub?: string; icon?: any }) {
  return (
    <div className="bg-surface rounded-xl border border-border p-4">
      <div className="flex items-center gap-2 text-text-tertiary text-[13px] mb-1">
        {Icon && <Icon size={14} />}
        {label}
      </div>
      <div className="text-[24px] font-semibold text-text-primary">{value}</div>
      {sub && <div className="text-[12px] text-text-tertiary mt-0.5">{sub}</div>}
    </div>
  );
}

function SectionHeader({ icon: Icon, title }: { icon: any; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon size={18} className="text-brand" />
      <h2 className="text-[16px] font-semibold text-text-primary">{title}</h2>
    </div>
  );
}

function StatusDot({ status }: { status: "pass" | "fail" | "warn" }) {
  const cls = status === "pass" ? "bg-brand" : status === "warn" ? "bg-warning" : "bg-danger";
  return <span className={`inline-block w-2 h-2 rounded-full ${cls}`} />;
}

function BreakdownBar({ data, colors }: { data: Record<string, number>; colors?: Record<string, string> }) {
  const total = Object.values(data).reduce((s, v) => s + v, 0);
  if (total === 0) return <div className="h-2 bg-surface-tertiary rounded-full" />;
  const defaultColors: Record<string, string> = {
    pass: "bg-brand", completed: "bg-brand", active: "bg-brand", up: "bg-brand",
    verified: "bg-brand", compliant: "bg-brand", approved: "bg-brand", ingested: "bg-brand",
    warn: "bg-warning", processing: "bg-warning", in_progress: "bg-warning",
    url_only: "bg-warning", paused: "bg-warning", debouncing: "bg-warning",
    preparing: "bg-warning", reviewing: "bg-warning",
    fail: "bg-danger", failed: "bg-danger", error: "bg-danger", down: "bg-danger",
    non_compliant: "bg-danger", rejected: "bg-danger", dead_link: "bg-danger",
    citation_only: "bg-warning-fill", stale: "bg-warning-fill",
    pending: "bg-surface-tertiary", missing: "bg-surface-tertiary",
    not_started: "bg-surface-tertiary", new: "bg-info/70",
    actionable: "bg-info", reviewed: "bg-info/40",
    dismissed: "bg-surface-tertiary",
  };
  const c = { ...defaultColors, ...colors };
  return (
    <div className="flex h-2 rounded-full overflow-hidden gap-px">
      {Object.entries(data).filter(([, v]) => v > 0).map(([key, val]) => (
        <div
          key={key}
          className={`${c[key] || "bg-surface-tertiary"} transition-all`}
          style={{ width: `${(val / total) * 100}%` }}
          title={`${key}: ${val}`}
        />
      ))}
    </div>
  );
}

function MiniTable({ data, label }: { data: Record<string, number>; label: string }) {
  const entries = Object.entries(data).sort(([, a], [, b]) => b - a);
  if (entries.length === 0) return null;
  return (
    <div className="text-[12px]">
      <div className="text-text-tertiary mb-1">{label}</div>
      {entries.map(([key, val]) => (
        <div key={key} className="flex justify-between py-0.5">
          <span className="text-text-secondary">{key}</span>
          <span className="text-text-primary font-medium">{val}</span>
        </div>
      ))}
    </div>
  );
}

const STORAGE_KEY = "enereg_admin_passphrase";

// ─── Main component ───

export function AdminDashboard() {
  const { resolved, setTheme } = useTheme();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passphrase, setPassphrase] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem(STORAGE_KEY));
  const [inputValue, setInputValue] = useState("");

  // Auto-load if we have a stored passphrase
  useEffect(() => {
    if (authed && passphrase) {
      setLoading(true);
      api.getAdminKpis(passphrase)
        .then(setData)
        .catch(err => {
          // Stored passphrase is stale
          sessionStorage.removeItem(STORAGE_KEY);
          setAuthed(false);
          setPassphrase("");
          setError(err.message);
        })
        .finally(() => setLoading(false));
    }
  }, [authed, passphrase]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const value = inputValue.trim();
    api.getAdminKpis(value)
      .then(result => {
        sessionStorage.setItem(STORAGE_KEY, value);
        setPassphrase(value);
        setAuthed(true);
        setData(result);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }

  // Passphrase prompt
  if (!authed) {
    return (
      <div className="min-h-screen bg-surface-secondary flex items-center justify-center">
        <form onSubmit={handleSubmit} className="bg-surface rounded-xl border border-border p-8 w-full max-w-sm">
          <h1 className="text-[18px] font-semibold text-text-primary mb-1">Admin Dashboard</h1>
          <p className="text-[13px] text-text-tertiary mb-6">Enter the admin passphrase to continue.</p>
          <input
            type="password"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            placeholder="Passphrase"
            autoFocus
            className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-primary text-[14px] mb-3 focus:outline-none focus:ring-2 focus:ring-brand/40"
          />
          {error && <p className="text-[13px] text-danger mb-3">{error}</p>}
          <button
            type="submit"
            disabled={loading || !inputValue.trim()}
            className="w-full py-2 rounded-lg bg-brand text-on-solid text-[14px] font-medium hover:bg-brand/90 disabled:opacity-50 transition-colors"
          >
            {loading ? "Verifying..." : "Continue"}
          </button>
        </form>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-surface-tertiary rounded w-48" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-surface-tertiary rounded-xl" />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-48 bg-surface-tertiary rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <ErrorMessage message={error} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-surface-tertiary rounded w-48" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-surface-tertiary rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  const { platform, countries, marketing_stats, engagement, monitoring, processing } = data;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-[22px] font-bold text-text-primary">Admin Dashboard</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setTheme(resolved === "dark" ? "light" : "dark")}
            className="p-1.5 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-surface-tertiary transition-colors"
            title={`Switch to ${resolved === "dark" ? "light" : "dark"} mode`}
          >
            {resolved === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <span className="text-[12px] text-text-tertiary">
            {new Date(data.generated_at).toLocaleString()}
          </span>
        </div>
      </div>

      {/* ─── Platform Overview ─── */}
      <section>
        <SectionHeader icon={LayoutDashboard} title="Platform Overview" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard label="Projects" value={platform.projects.total} icon={FolderOpen}
            sub={Object.entries(platform.projects.by_status).filter(([, v]) => (v as number) > 0).map(([k, v]) => `${v} ${k}`).join(", ")} />
          <StatCard label="Users" value={platform.users.total} icon={Users}
            sub={Object.entries(platform.users.by_role).map(([k, v]) => `${v} ${k}`).join(", ")} />
          <StatCard label="Organizations" value={platform.organizations} icon={Building2} />
          <StatCard label="Documents" value={platform.documents.total} icon={FileText}
            sub={platform.documents.by_extraction_status.completed ? `${platform.documents.by_extraction_status.completed} extracted` : undefined} />
          <StatCard label="Avg Field Completion" value={`${platform.field_completion.avg_completion_pct}%`}
            sub={`${platform.field_completion.total_fields} universal fields`} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <div className="bg-surface rounded-xl border border-border p-4">
            <MiniTable data={platform.projects.by_country} label="Projects by country" />
          </div>
          <div className="bg-surface rounded-xl border border-border p-4">
            <MiniTable data={platform.projects.by_energy_type} label="Projects by energy type" />
          </div>
          <div className="bg-surface rounded-xl border border-border p-4">
            <MiniTable data={platform.documents.by_extraction_status} label="Document extraction status" />
          </div>
        </div>
      </section>

      {/* ─── Country Readiness ─── */}
      <section>
        <SectionHeader icon={Globe} title="Country Readiness" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {countries.map((c: any) => (
            <div key={c.country_code} className="bg-surface rounded-xl border border-border p-4">
              <div className="flex items-center gap-3 mb-3">
                <HealthRing percentage={c.score} size={44} strokeWidth={4} />
                <div>
                  <div className="text-[15px] font-semibold text-text-primary">{c.country_name}</div>
                  <div className="text-[12px] text-text-tertiary">
                    {c.ready
                      ? <span className="text-brand font-medium">Production ready</span>
                      : <span className="text-danger font-medium">Not ready</span>
                    }
                  </div>
                </div>
              </div>
              {/* Checks */}
              <div className="space-y-1 mb-3">
                {c.checks.map((ch: any) => (
                  <div key={ch.category} className="flex items-center gap-2 text-[12px]">
                    <StatusDot status={ch.status} />
                    <span className="text-text-secondary flex-1">{ch.category}</span>
                    <span className={ch.status === "pass" ? "text-brand font-medium" : ch.status === "warn" ? "text-warning-text" : "text-danger"}>
                      {ch.count}
                    </span>
                    {ch.status !== "pass" && (
                      <span className="text-text-tertiary">need {ch.minimum}</span>
                    )}
                  </div>
                ))}
              </div>
              {/* Data quality bar */}
              {c.data_quality.compliance_total > 0 && (
                <div>
                  <div className="text-[11px] text-text-tertiary mb-1">Source quality</div>
                  <BreakdownBar data={c.data_quality.source_quality} />
                  <div className="flex gap-3 mt-1 text-[11px] text-text-tertiary">
                    {Object.entries(c.data_quality.source_quality).map(([k, v]) => (
                      <span key={k}>{k}: {v as number}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ─── Marketing Stats ─── */}
      <section>
        <SectionHeader icon={BarChart3} title="Marketing Stats" />
        <p className="text-[12px] text-text-tertiary mb-3">
          These are the live values served to enereg.com via /api/stats
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Countries" value={marketing_stats.countries}
            sub={marketing_stats.country_codes.join(", ")} />
          <StatCard label="Field Definitions" value={marketing_stats.field_definitions} />
          <StatCard label="License Types" value={marketing_stats.license_types}
            sub="across ready countries" />
          <StatCard label="Compliance Frameworks" value={marketing_stats.compliance_frameworks}
            sub="across ready countries" />
        </div>
      </section>

      {/* ─── Engagement ─── */}
      <section>
        <SectionHeader icon={MessageSquare} title="Engagement" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Interviews */}
          <div className="bg-surface rounded-xl border border-border p-4">
            <div className="text-[13px] text-text-tertiary mb-2">Interview Responses</div>
            <div className="text-[24px] font-semibold text-text-primary mb-2">{engagement.interviews.total}</div>
            {engagement.interviews.by_country.length > 0 && (
              <MiniTable
                data={Object.fromEntries(engagement.interviews.by_country.map((c: any) => [c.country_code, c.count]))}
                label="By country"
              />
            )}
          </div>
          {/* Feedback */}
          <div className="bg-surface rounded-xl border border-border p-4">
            <div className="text-[13px] text-text-tertiary mb-2">User Feedback</div>
            <div className="text-[24px] font-semibold text-text-primary mb-2">{engagement.feedback.total}</div>
            <div className="flex gap-4 text-[13px] mb-3">
              <span className="flex items-center gap-1 text-brand"><ThumbsUp size={13} /> {engagement.feedback.up}</span>
              <span className="flex items-center gap-1 text-danger"><ThumbsDown size={13} /> {engagement.feedback.down}</span>
            </div>
            {engagement.feedback.by_entity_type.length > 0 && (
              <div className="text-[12px] space-y-1">
                <div className="text-text-tertiary">By type</div>
                {engagement.feedback.by_entity_type.map((e: any) => (
                  <div key={e.entity_type} className="flex justify-between">
                    <span className="text-text-secondary">{e.entity_type}</span>
                    <span className="text-text-tertiary">{e.up}↑ {e.down}↓</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Waitlist */}
          <div className="bg-surface rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-[13px] text-text-tertiary mb-2">
              <Mail size={14} /> Waitlist
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-[24px] font-semibold text-text-primary">{engagement.waitlist.total}</span>
              <span className="text-[12px] text-text-tertiary">{engagement.waitlist.recent_7d} in last 7 days</span>
            </div>
            {engagement.waitlist.items?.length > 0 && (
              <div className="mt-3 space-y-0.5">
                {engagement.waitlist.items.map((w: any, i: number) => {
                  // decorative rotation — decorative-safe hues only: no ai
                  // (purple = machine-authored, nothing else) and no amber
                  // (attention). signal exists for exactly this.
                  const colors = ["border-brand", "border-info", "border-signal", "border-navy-400"];
                  const d = new Date(w.created_at);
                  const mon = d.toLocaleString("en", { month: "short" }).toLowerCase();
                  const day = String(d.getDate()).padStart(2, "0");
                  return (
                    <div key={w.id} className={`flex items-center gap-2 text-[12px] py-1 pl-2 border-l-2 ${colors[i % colors.length]}`}>
                      <span className="text-text-primary font-medium truncate">{w.email}</span>
                      <span className="text-text-tertiary">—</span>
                      <span className="text-text-secondary truncate">{w.name || "—"}</span>
                      <span className="text-text-tertiary">—</span>
                      <span className="text-text-secondary truncate">{w.company || "—"}</span>
                      <span className="text-text-tertiary ml-auto whitespace-nowrap">{mon}-{day}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── Regulatory Monitoring ─── */}
      <section>
        <SectionHeader icon={Radio} title="Regulatory Monitoring" />
        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <StatCard label="Monitors" value={monitoring.monitors.total} icon={Radio}
            sub={`${monitoring.monitors.by_status.active || 0} active, ${monitoring.monitors.by_status.error || 0} errored`} />
          <StatCard label="Events Detected" value={monitoring.events.total}
            sub={`${monitoring.events.recent_7d} in last 7 days`} />
          <StatCard label="Regulation Sources" value={monitoring.sources.total}
            sub={`${monitoring.sources.by_status?.ingested || 0} ingested`} />
          <div className="bg-surface rounded-xl border border-border p-4">
            <div className="text-[13px] text-text-tertiary mb-1">Source Pipeline</div>
            <BreakdownBar data={monitoring.sources.by_status || {}} />
            <div className="mt-2">
              <MiniTable data={monitoring.sources.by_status || {}} label="" />
            </div>
          </div>
        </div>

        {/* Monitor status table */}
        {monitoring.monitors.items && (
          <div className="bg-surface rounded-xl border border-border p-4 mb-4">
            <div className="text-[13px] text-text-tertiary mb-3">Monitor Status</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-text-tertiary text-left border-b border-border">
                    <th className="pb-2 pr-3 font-medium">Country</th>
                    <th className="pb-2 pr-3 font-medium">Monitor</th>
                    <th className="pb-2 pr-3 font-medium">Status</th>
                    <th className="pb-2 pr-3 font-medium">Last Checked</th>
                    <th className="pb-2 pr-3 font-medium">Last Changed</th>
                    <th className="pb-2 font-medium">Errors</th>
                  </tr>
                </thead>
                <tbody className="text-text-secondary">
                  {monitoring.monitors.items.map((m: any) => (
                    <tr key={m.id} className="border-b border-border/50">
                      <td className="py-1.5 pr-3 font-medium">{m.country_code}</td>
                      <td className="py-1.5 pr-3">
                        <a href={m.target_url} target="_blank" rel="noopener noreferrer"
                          className="text-brand hover:underline">{m.name}</a>
                      </td>
                      <td className="py-1.5 pr-3">
                        <span className={`inline-flex items-center gap-1 ${
                          m.status === "active" ? "text-brand" : m.status === "error" ? "text-danger" : "text-text-tertiary"
                        }`}>
                          <StatusDot status={m.status === "active" ? "pass" : m.status === "error" ? "fail" : "warn"} />
                          {m.status}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3 text-text-tertiary">
                        {m.last_checked_at ? new Date(m.last_checked_at).toLocaleDateString() : "Never"}
                      </td>
                      <td className="py-1.5 pr-3 text-text-tertiary">
                        {m.last_changed_at ? new Date(m.last_changed_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-1.5">
                        {m.error_count > 0 ? (
                          <span className="text-danger" title={m.last_error || ""}>{m.error_count}</span>
                        ) : "0"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent events feed */}
        {monitoring.events.recent?.length > 0 && (
          <div className="bg-surface rounded-xl border border-border p-4">
            <div className="text-[13px] text-text-tertiary mb-3">Recent Events</div>
            <div className="space-y-2">
              {monitoring.events.recent.map((e: any) => (
                <div key={e.id} className="flex items-start gap-2 text-[12px] py-1.5 border-b border-border/50 last:border-0">
                  <StatusDot status={e.status === "actionable" ? "fail" : e.status === "dismissed" ? "pass" : "warn"} />
                  <div className="flex-1 min-w-0">
                    <div className="text-text-primary font-medium">{e.title}</div>
                    {e.summary && <div className="text-text-tertiary mt-0.5 truncate">{e.summary}</div>}
                  </div>
                  <div className="text-text-tertiary whitespace-nowrap">
                    {e.detected_at ? new Date(e.detected_at).toLocaleDateString() : ""}
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    e.status === "actionable" ? "bg-danger-light text-danger"
                    : e.status === "dismissed" ? "bg-surface-tertiary text-text-tertiary"
                    : e.status === "new" ? "bg-info-light text-info"
                    : "bg-surface-tertiary text-text-secondary"
                  }`}>{e.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ─── Processing ─── */}
      <section>
        <SectionHeader icon={Cpu} title="Processing" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-surface rounded-xl border border-border p-4">
            <div className="text-[13px] text-text-tertiary mb-2">Job Queue</div>
            <div className="text-[24px] font-semibold text-text-primary mb-2">{processing.jobs.total}</div>
            <BreakdownBar data={processing.jobs.by_status} />
            <div className="mt-2">
              <MiniTable data={processing.jobs.by_status} label="By status" />
            </div>
            <div className="text-[12px] text-text-tertiary mt-2">
              Total LLM cost: ${processing.jobs.total_cost_usd.toFixed(3)}
            </div>
          </div>
          <div className="bg-surface rounded-xl border border-border p-4">
            <div className="text-[13px] text-text-tertiary mb-2">Evaluations</div>
            <div className="text-[24px] font-semibold text-text-primary mb-2">{processing.evaluations.total}</div>
            <div className="text-[12px] text-text-tertiary">
              Total LLM cost: ${processing.evaluations.total_cost_usd.toFixed(3)}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
